'use client';

/**
 * ReferenceExperience — the shell of the StudyNinja living demo.
 *
 * Owns the demo state, the scene transition, the pointer bus (CSS vars written
 * in rAF, never React state), the keyboard map, dialogs (map, assistant, help,
 * mobile sheet, explorer), deep links, announcements and the LivingContext
 * every module under `../living` reads. The ten canonical PNGs stay the visual
 * source of truth; the living modules are mounted on top of them.
 *
 * Deconstruct mode (the former semantic reconstruction) is a deep mode: key D,
 * the dock button (window event `sn:deconstruct`) or `?mode=deconstruct`.
 */

import dynamic from 'next/dynamic';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
} from 'react';
import type { Lang } from '@/i18n/config';
import { LivingContext } from '../living/context';
import { buildDeepLink, parseDeepLink } from '../living/deeplink';
import Dock from '../living/dock/Dock';
import LensCard from '../living/lens/LensCard';
import NeonLayer, { LIGHT_META } from '../living/light/NeonLayer';
import { LENS_BOOK, PERSONA_META } from '../living/personas';
import { useTour } from '../living/tour/useTour';
import {
  CANVAS_UNIT_VAR,
  DEFAULT_PERSONA,
  PERSONAS,
  SCENE_CANVAS,
  isPersona,
  type LivingContextValue,
  type Persona,
} from '../living/types';
import ReferenceRaster, { REFERENCE_RASTER_META } from './ReferenceRaster';
import { hotspotsFor } from './hotspots';
import {
  INITIAL_REFERENCE_STATE,
  REFERENCE_SCENES,
  REFERENCE_SCENE_LABELS,
  REFERENCE_TRANSCRIPTS,
  validateReferenceActions,
  type ReferenceDemoPatch,
  type ReferenceDemoState,
  type ReferencePresentationAction,
  type ReferenceSceneId,
} from './types';
import styles from './ReferenceExperience.module.css';

/* The semantic reconstruction is the deep mode now, not the default path: load it on demand. */
const ReferenceStage = dynamic(() => import('./ReferenceStage'), { ssr: false });

interface Exchange {
  id: number;
  question: string;
  answer: string;
  state: 'streaming' | 'done' | 'error' | 'cancelled';
}

type DialogKind = 'map' | 'assistant' | 'help' | 'sheet' | 'explorer';

interface Transition {
  /** Snapshot of the outgoing scene state, null once settled. */
  prev: ReferenceDemoState | null;
  dir: 1 | -1;
  run: number;
}

const PERSONA_KEY = 'sn-persona';
const NUDGE_HINT_KEY = 'sn-keys-hint';
const MODE_PARAM = 'mode';
const TOUR_PARAM = 'tour';
const VIEWPORT_ASPECT = 1672 / 941;
const BOOT_MS = 1600;
const ENTER_FALLBACK_MS = 700;
const ENTER_FALLBACK_REDUCED_MS = 240;
const DECODE_RACE_MS = 300;
const DOCK_SHOW_MS = 3000;
const DOCK_HIDE_MS = 2400;
const TOAST_MS = 2600;
const DECONSTRUCT_DISSOLVE_MS = 520;

const FALLBACKS: Record<Lang, string> = {
  ru: 'AI‑слой временно недоступен. Все десять подготовленных сценариев и интерактивные модели продолжают работать локально.',
  en: 'The AI layer is temporarily unavailable. All ten prepared scenarios and interactive models remain available locally.',
};

const CANCELLED: Record<Lang, string> = {
  ru: 'Запрос остановлен.',
  en: 'Request stopped.',
};

const ACTS: ReadonlyArray<{ scenes: readonly number[]; ru: string; en: string }> = [
  { scenes: [0, 1, 2], ru: 'I · Увидеть', en: 'I. See' },
  { scenes: [3, 4, 5], ru: 'II · Связать', en: 'II. Connect' },
  { scenes: [6, 7], ru: 'III · Почувствовать и решить', en: 'III. Feel and decide' },
  { scenes: [8, 9], ru: 'IV · Честно и дальше', en: 'IV. Honest and onward' },
];

const COPY = {
  ru: {
    skipTranscript: 'К описанию текущей сцены',
    skipDock: 'К панели управления',
    sceneRole: 'сцена',
    regions: 'Интерактивные области экрана',
    prev: 'Предыдущая сцена',
    next: 'Следующая сцена',
    lens: 'Линза',
    mapTitle: 'Карта сцен StudyNinja',
    close: 'Закрыть',
    assistantTitle: 'Спросите StudyNinja',
    scene: 'СЦЕНА',
    assistantEmpty: 'Вопрос может переключить сцену, сравнить маршруты или изменить глубину объяснения.',
    thinking: 'Анализирую…',
    aiNotice: 'Сцены работают локально. Текст вопроса обрабатывается через защищённый серверный AI‑адаптер — не вводите персональные данные.',
    placeholder: 'Например: сравни два маршрута',
    send: 'Отправить',
    helpTitle: 'Клавиши и жесты',
    helpGroups: {
      nav: 'Навигация',
      story: 'Рассказ',
      lens: 'Взгляд',
      share: 'Поделиться',
    },
    help: {
      arrows: 'Предыдущая / следующая сцена',
      digits: 'Перейти к сцене 1–10',
      homeEnd: 'Первая / последняя сцена',
      shiftArrows: 'Шаг состояния внутри сцены',
      map: 'Карта сцен',
      space: 'Рассказ: пуск / пауза',
      deconstruct: 'Разобрать сцену',
      reset: 'Сброс демо',
      persona: 'Сменить линзу',
      ask: 'Спросить AI',
      fullscreen: 'Полный экран',
      share: 'Скопировать ссылку',
      help: 'Эта справка',
      esc: 'Закрыть верхний слой',
      swipe: 'Свайп по ленте: смена сцены',
      tap: 'Тап по сцене: исследовать с масштабом',
      pinch: 'Щипок в исследователе: масштаб',
    },
    deconstructCrumb: 'Эталон',
    backToReference: 'Эталонный экран',
    deconstructLabel: 'Разобрать сцену',
    explorerTitle: 'Исследование сцены',
    explorerClose: 'Закрыть исследование',
    controls: 'Управлять сценой',
    hideControls: 'Скрыть управление',
    allScenes: 'Все сцены',
    ask: 'Спросить AI',
    share: 'Поделиться',
    more: 'Ещё',
    play: 'Рассказ',
    pause: 'Пауза',
    sheetTitle: 'Управление сценой',
    tourBar: 'Ход рассказа',
    toastCopied: (n: number, lens: string) => `Ссылка скопирована: сцена ${n}, линза «${lens}»`,
    toastShared: (n: number, lens: string) => `Ссылка отправлена: сцена ${n}, линза «${lens}»`,
    toastFailed: (url: string) => `Не удалось скопировать. Ссылка: ${url}`,
    keysHint: 'Нажмите ? — список клавиш',
    announceScene: (n: number, name: string, lens: string) => `Сцена ${n} из 10: ${name}. Линза: ${lens}.`,
    announcePersona: (label: string, focus: string) => `Линза: ${label} — ${focus}.`,
    announceDeconstruct: (name: string) => `Режим разбора: ${name}. Esc вернёт эталонный экран.`,
    announceReference: (name: string) => `Эталонный экран: ${name}.`,
    announceTourStart: 'Рассказ запущен. Пробел — пауза.',
    announceTourPause: 'Рассказ на паузе.',
    announceExplorerOpen: (name: string) => `Исследование сцены: ${name}. Esc закроет.`,
    announceExplorerClose: 'Исследование закрыто.',
    announceReset: 'Демо сброшено к первой сцене.',
    announceFullscreenOn: 'Полноэкранный режим включён.',
    announceFullscreenOff: 'Полноэкранный режим выключен.',
    personaGroup: 'Линза просмотра',
    dots: 'Сцены',
    goToScene: (n: number, name: string) => `Сцена ${n}: ${name}`,
  },
  en: {
    skipTranscript: 'Skip to current scene description',
    skipDock: 'Skip to the control dock',
    sceneRole: 'scene',
    regions: 'Interactive screen regions',
    prev: 'Previous scene',
    next: 'Next scene',
    lens: 'Lens',
    mapTitle: 'StudyNinja scene map',
    close: 'Close',
    assistantTitle: 'Ask StudyNinja',
    scene: 'SCENE',
    assistantEmpty: 'A question can switch scenes, compare routes, or change explanation depth.',
    thinking: 'Thinking…',
    aiNotice: 'Scenes run locally. Question text is processed through a protected server-side AI adapter. Do not enter personal data.',
    placeholder: 'For example: compare two routes',
    send: 'Send',
    helpTitle: 'Keys and gestures',
    helpGroups: {
      nav: 'Navigation',
      story: 'Story',
      lens: 'Lens',
      share: 'Share',
    },
    help: {
      arrows: 'Previous / next scene',
      digits: 'Jump to scene 1 to 10',
      homeEnd: 'First / last scene',
      shiftArrows: 'Step the state inside the scene',
      map: 'Scene map',
      space: 'Tour: play / pause',
      deconstruct: 'Deconstruct the scene',
      reset: 'Reset the demo',
      persona: 'Cycle the lens',
      ask: 'Ask AI',
      fullscreen: 'Fullscreen',
      share: 'Copy the link',
      help: 'This help',
      esc: 'Close the top layer',
      swipe: 'Swipe the strip: change scene',
      tap: 'Tap the scene: explore with zoom',
      pinch: 'Pinch in the explorer: zoom',
    },
    deconstructCrumb: 'Reference',
    backToReference: 'Reference view',
    deconstructLabel: 'Deconstruct scene',
    explorerTitle: 'Scene explorer',
    explorerClose: 'Close the explorer',
    controls: 'Control scene',
    hideControls: 'Hide controls',
    allScenes: 'All scenes',
    ask: 'Ask AI',
    share: 'Share',
    more: 'More',
    play: 'Tour',
    pause: 'Pause',
    sheetTitle: 'Scene controls',
    tourBar: 'Tour progress',
    toastCopied: (n: number, lens: string) => `Link copied: scene ${n}, ${lens} lens`,
    toastShared: (n: number, lens: string) => `Link shared: scene ${n}, ${lens} lens`,
    toastFailed: (url: string) => `Copy failed. Link: ${url}`,
    keysHint: 'Press ? for the key list',
    announceScene: (n: number, name: string, lens: string) => `Scene ${n} of 10: ${name}. Lens: ${lens}.`,
    announcePersona: (label: string, focus: string) => `Lens: ${label}. ${focus}.`,
    announceDeconstruct: (name: string) => `Deconstruct mode: ${name}. Escape returns to the reference view.`,
    announceReference: (name: string) => `Reference view: ${name}.`,
    announceTourStart: 'Tour started. Space pauses.',
    announceTourPause: 'Tour paused.',
    announceExplorerOpen: (name: string) => `Scene explorer: ${name}. Escape closes it.`,
    announceExplorerClose: 'Explorer closed.',
    announceReset: 'Demo reset to the first scene.',
    announceFullscreenOn: 'Fullscreen on.',
    announceFullscreenOff: 'Fullscreen off.',
    personaGroup: 'Viewing lens',
    dots: 'Scenes',
    goToScene: (n: number, name: string) => `Scene ${n}: ${name}`,
  },
} as const;

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function trapDialogFocus(event: ReactKeyboardEvent<HTMLElement>) {
  if (event.key !== 'Tab') return;
  const focusable = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    .filter((element) => element.offsetParent !== null && !element.hasAttribute('inert'));
  if (focusable.length === 0) {
    event.preventDefault();
    event.currentTarget.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function inferScene(question: string): ReferenceSceneId | null {
  const q = question.toLocaleLowerCase('ru');
  if (/класс|одинаков.*оцен|сравн.*учен|student.*compare/.test(q)) return 'classroom';
  if (/мотивац.*пропад|цепоч|почему.*не хочет/.test(q)) return 'motivation';
  if (/цель учител|направлен|педагогическ.*цель/.test(q)) return 'teacher-goal';
  if (/граф|карт.*знан|сло[ий]|предпосыл/.test(q)) return 'knowledge-map';
  if (/маршрут|траектор|экзамен/.test(q)) return 'personal-route';
  if (/цикл|коррекц|следующ.*шаг/.test(q)) return 'learning-cycle';
  if (/я могу|субъект|награ|уверенн/.test(q)) return 'agency';
  if (/перед урок|решен.*учител|рабоч.*простран|сигнал/.test(q)) return 'teacher-workspace';
  if (/технолог|тех.*эксперт|исслед|как.*работ/.test(q)) return 'technology';
  if (/итог|связ.*учител|финал/.test(q)) return 'finale';
  return null;
}

function localPatch(question: string): ReferenceDemoPatch {
  const q = question.toLocaleLowerCase('ru');
  if (/сравн/.test(q)) return { compareRoute: true };
  if (/разб.*сло|explode/.test(q)) {
    return { graphLayers: { concepts: true, prerequisites: true, methods: true, errors: true, examples: true } };
  }
  if (/техническ|technical/.test(q)) return { explanationDepth: 'technical' };
  if (/методист/.test(q)) return { explanationDepth: 'methodologist' };
  return {};
}

function mergeDemoPatch(current: ReferenceDemoState, patch: ReferenceDemoPatch): ReferenceDemoState {
  return {
    ...current,
    ...patch,
    profile: patch.profile ? { ...current.profile, ...patch.profile } : current.profile,
    graphLayers: patch.graphLayers ? { ...current.graphLayers, ...patch.graphLayers } : current.graphLayers,
  };
}

const LEARNER_NAMES = ['Анна', 'Богдан', 'Виктория', 'Даниил', 'Ева', 'Захар', 'Ирина', 'Кирилл', 'Лера', 'Максим', 'Назар', 'Ольга'] as const;
const LEARNER_NAMES_EN = ['Anna', 'Bogdan', 'Victoria', 'Daniil', 'Eva', 'Zakhar', 'Irina', 'Kirill', 'Lera', 'Maxim', 'Nazar', 'Olga'] as const;
const DEPTHS: ReferenceDemoState['explanationDepth'][] = ['teacher', 'methodologist', 'technical'];
const DEPTH_LABEL: Record<ReferenceDemoState['explanationDepth'], Record<Lang, string>> = {
  teacher: { ru: 'учитель', en: 'teacher' },
  methodologist: { ru: 'методист', en: 'methodologist' },
  technical: { ru: 'технический эксперт', en: 'technical expert' },
};

/** One sentence describing the state that matters on the current scene (live region + mobile sheet). */
function describeState(state: ReferenceDemoState, lang: Lang): string {
  const ru = lang === 'ru';
  switch (state.sceneId) {
    case 'classroom': {
      const name = ru ? LEARNER_NAMES[state.selectedStudent - 1] : LEARNER_NAMES_EN[state.selectedStudent - 1];
      return ru ? `Выбран ученик ${state.selectedStudent}: ${name}.` : `Learner ${state.selectedStudent} selected: ${name}.`;
    }
    case 'motivation':
      return ru ? `Выбрано звено ${state.causeStep + 1} из 7.` : `Cause ${state.causeStep + 1} of 7 is selected.`;
    case 'teacher-goal':
      return ru ? `Выбрана цель ${state.teacherGoal + 1} из 5.` : `Goal ${state.teacherGoal + 1} of 5 is selected.`;
    case 'knowledge-map':
      return ru
        ? `Режим: ${state.graphMode === 'teacher' ? 'учитель' : 'ученик'}; слой ошибок ${state.graphLayers.errors ? 'показан' : 'скрыт'}; карточка узла ${state.graphDrawerOpen ? 'открыта' : 'закрыта'}.`
        : `Mode: ${state.graphMode}; the errors layer is ${state.graphLayers.errors ? 'shown' : 'hidden'}; node details are ${state.graphDrawerOpen ? 'open' : 'closed'}.`;
    case 'personal-route':
      return ru
        ? `Уверенность ${state.profile.confidence} из 6; сравнение ${state.compareRoute ? 'включено' : 'выключено'}.`
        : `Confidence is ${state.profile.confidence} of 6; comparison is ${state.compareRoute ? 'on' : 'off'}.`;
    case 'learning-cycle':
      return ru ? `Активен этап ${state.cycleStep + 1} из 8.` : `Stage ${state.cycleStep + 1} of 8 is active.`;
    case 'agency':
      return ru
        ? `Подкрепление: ${state.rewardMode === 'mastery' ? 'за освоение' : 'за действие'}.`
        : `Reinforcement: ${state.rewardMode === 'mastery' ? 'mastery' : 'action'}.`;
    case 'teacher-workspace':
      return ru ? `Выбрано вмешательство ${state.teacherAction + 1} из 4.` : `Intervention ${state.teacherAction + 1} of 4 is selected.`;
    case 'technology':
      return ru ? `Глубина объяснения: ${DEPTH_LABEL[state.explanationDepth].ru}.` : `Explanation depth: ${DEPTH_LABEL[state.explanationDepth].en}.`;
    case 'finale':
      return ru ? 'Единый адаптивный цикл готов к новому вопросу.' : 'The adaptive loop is ready for another question.';
  }
}

/** Shift+Arrow: the in-scene state stepped by ±1 (learner, cause, goal, node, profile field, cycle step, reward, action, depth). */
function stepInScene(state: ReferenceDemoState, delta: 1 | -1): ReferenceDemoPatch {
  const wrap = (value: number, size: number) => (value + delta + size) % size;
  switch (state.sceneId) {
    case 'classroom': return { selectedStudent: wrap(state.selectedStudent - 1, 12) + 1 };
    case 'motivation': return { causeStep: wrap(state.causeStep, 7) };
    case 'teacher-goal': return { teacherGoal: wrap(state.teacherGoal, 5) };
    case 'knowledge-map': return { graphLayers: { errors: !state.graphLayers.errors } };
    case 'personal-route': return { profile: { confidence: wrap(state.profile.confidence, 7) } };
    case 'learning-cycle': return { cycleStep: wrap(state.cycleStep, 8) };
    case 'agency': return { rewardMode: state.rewardMode === 'mastery' ? 'action' : 'mastery' };
    case 'teacher-workspace': return { teacherAction: wrap(state.teacherAction, 4) };
    case 'technology': return { explanationDepth: DEPTHS[wrap(DEPTHS.indexOf(state.explanationDepth), DEPTHS.length)] };
    case 'finale': return {};
  }
}

/** Persona applies one benign patch so its evidence is visible on the technology and knowledge-map scenes. */
function personaPatch(persona: Persona): ReferenceDemoPatch {
  return {
    explanationDepth: persona === 'expert' ? 'methodologist' : persona === 'investor' ? 'technical' : 'teacher',
    graphMode: persona === 'parent' ? 'student' : 'teacher',
  };
}

function personaMeta(persona: Persona) {
  return PERSONA_META.find((meta) => meta.id === persona) ?? PERSONA_META[0];
}

/** Canvas percentage → viewport percentage (the classroom canvas is narrower than the 1672/941 viewport). */
function canvasToViewport(scene: ReferenceSceneId, x: number, y: number): { x: number; y: number; scale: number } {
  const canvas = SCENE_CANVAS[scene];
  const frac = (canvas.width / canvas.height) / VIEWPORT_ASPECT;
  const offset = (1 - frac) / 2;
  return { x: (offset + x * frac) * 100, y: y * 100, scale: frac };
}

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string) {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage is a convenience: the URL still carries the state.
  }
}

/* ── Modal layer: inert siblings, initial focus, return focus ─────────────── */

function useModalLayer(
  kind: DialogKind | null,
  dialogRef: RefObject<HTMLElement | null>,
  returnFocusRef: RefObject<HTMLElement | null>,
  fallbackRef: RefObject<HTMLElement | null>,
  initialFocus: string,
) {
  useEffect(() => {
    if (kind === null) return;
    const dialog = dialogRef.current;
    const scrim = dialog?.parentElement;
    if (!dialog || !scrim) return;

    const snapshots: Array<{ element: HTMLElement; inert: boolean; ariaHidden: string | null }> = [];
    let branch: HTMLElement = scrim;
    while (branch.parentElement) {
      const parent = branch.parentElement;
      for (const sibling of Array.from(parent.children)) {
        if (!(sibling instanceof HTMLElement) || sibling === branch) continue;
        snapshots.push({ element: sibling, inert: sibling.inert, ariaHidden: sibling.getAttribute('aria-hidden') });
        sibling.inert = true;
        sibling.setAttribute('aria-hidden', 'true');
      }
      if (parent === document.body) break;
      branch = parent;
    }

    const focusFrame = requestAnimationFrame(() => {
      const target = dialog.querySelector<HTMLElement>(initialFocus) ?? dialog.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? dialog;
      target.focus();
    });

    return () => {
      cancelAnimationFrame(focusFrame);
      for (const snapshot of snapshots.reverse()) {
        snapshot.element.inert = snapshot.inert;
        if (snapshot.ariaHidden === null) snapshot.element.removeAttribute('aria-hidden');
        else snapshot.element.setAttribute('aria-hidden', snapshot.ariaHidden);
      }
      const previous = returnFocusRef.current;
      returnFocusRef.current = null;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Another dialog replaced this one (sheet → map): it owns focus now.
          if (dialogRef.current && dialogRef.current !== dialog && dialogRef.current.isConnected) return;
          const usable = (element: HTMLElement | null | undefined): element is HTMLElement =>
            Boolean(element?.isConnected) && !element!.closest('[inert]');
          if (usable(previous)) previous.focus();
          else {
            const dockAsk = document.querySelector<HTMLElement>('[data-testid="sn-dock-ask"]');
            if (usable(dockAsk)) dockAsk.focus();
            else fallbackRef.current?.focus();
          }
        });
      });
    };
  }, [dialogRef, fallbackRef, initialFocus, kind, returnFocusRef]);
}

/* ── Shell ────────────────────────────────────────────────────────────────── */

export default function ReferenceExperience({ lang }: { lang: Lang }) {
  const t = COPY[lang];
  const rootRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const transcriptRef = useRef<HTMLElement>(null);
  const semanticStageRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const explorerScrollRef = useRef<HTMLDivElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const navAbortRef = useRef<AbortController | null>(null);
  const stateRef = useRef<ReferenceDemoState>(INITIAL_REFERENCE_STATE);
  const personaRef = useRef<Persona>(DEFAULT_PERSONA);
  const dockTimerRef = useRef<number>(0);
  const dockLastMoveRef = useRef<number>(0);
  const dockVisibleRef = useRef(false);
  const toastTimerRef = useRef<number>(0);
  const enterTimerRef = useRef<number>(0);
  const announceFramesRef = useRef<number[]>([]);
  const dialogOpenRef = useRef(false);
  const hydratedRef = useRef(false);
  const firstSceneAnnouncedRef = useRef(false);
  const sceneChangesRef = useRef(0);
  const stripProgrammaticRef = useRef(false);
  const stripTimerRef = useRef<number>(0);
  const pendingTourRef = useRef(false);

  const [state, setState] = useState<ReferenceDemoState>(INITIAL_REFERENCE_STATE);
  const [persona, setPersonaState] = useState<Persona>(DEFAULT_PERSONA);
  const [transition, setTransition] = useState<Transition>({ prev: null, dir: 1, run: 0 });
  const [neonPhase, setNeonPhase] = useState<'off' | 'on'>('on');
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [deconstructOpen, setDeconstructOpen] = useState(false);
  const [deconstructSettled, setDeconstructSettled] = useState(false);
  const [questionSeed, setQuestionSeed] = useState('');
  const [question, setQuestion] = useState('');
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [busy, setBusy] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [touch, setTouch] = useState(false);
  const [boot, setBoot] = useState(true);
  const [dockVisible, setDockVisible] = useState(false);
  const [toast, setToast] = useState<{ id: number; text: string } | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const [visitedScenes, setVisitedScenes] = useState<ReferenceSceneId[]>([INITIAL_REFERENCE_STATE.sceneId]);
  const [fullscreen, setFullscreen] = useState(false);
  const compactModeRef = useRef(false);
  compactModeRef.current = compactMode;

  const sceneIndex = REFERENCE_SCENES.indexOf(state.sceneId);
  const sceneName = REFERENCE_SCENE_LABELS[state.sceneId][lang];
  const transcript = REFERENCE_TRANSCRIPTS[state.sceneId][lang];
  const personaLabel = personaMeta(persona).label[lang];
  const reducedMotionRef = useRef(false);
  reducedMotionRef.current = reducedMotion;

  useEffect(() => { stateRef.current = state; }, [state]);
  useEffect(() => { personaRef.current = persona; }, [persona]);
  useEffect(() => { dialogOpenRef.current = dialog !== null; }, [dialog]);

  /* ── announcements (clear, then set across two frames) ── */
  const announce = useCallback((text: string) => {
    for (const frame of announceFramesRef.current) cancelAnimationFrame(frame);
    announceFramesRef.current = [];
    setAnnouncement('');
    const first = requestAnimationFrame(() => {
      const second = requestAnimationFrame(() => setAnnouncement(text));
      announceFramesRef.current.push(second);
    });
    announceFramesRef.current.push(first);
  }, []);

  useEffect(() => () => {
    for (const frame of announceFramesRef.current) cancelAnimationFrame(frame);
  }, []);

  const showToast = useCallback((text: string) => {
    window.clearTimeout(toastTimerRef.current);
    setToast({ id: Date.now(), text });
    toastTimerRef.current = window.setTimeout(() => setToast(null), TOAST_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimerRef.current), []);

  /* ── dock presence ── */
  const revealDock = useCallback((ms: number) => {
    dockLastMoveRef.current = performance.now();
    if (!dockVisibleRef.current) {
      dockVisibleRef.current = true;
      setDockVisible(true);
    }
    window.clearTimeout(dockTimerRef.current);
    const arm = (delay: number) => {
      dockTimerRef.current = window.setTimeout(() => {
        const elapsed = performance.now() - dockLastMoveRef.current;
        if (elapsed < ms) {
          arm(ms - elapsed);
          return;
        }
        dockVisibleRef.current = false;
        setDockVisible(false);
      }, delay);
    };
    arm(ms);
  }, []);

  useEffect(() => () => window.clearTimeout(dockTimerRef.current), []);

  /* ── state patches ── */
  const patchState = useCallback((patch: ReferenceDemoPatch) => {
    setState((current) => mergeDemoPatch(current, patch));
  }, []);

  /* ── scene navigation with crossfade ── */
  const commitScene = useCallback((next: ReferenceSceneId, dir: 1 | -1, animated: boolean) => {
    const current = stateRef.current;
    performance.mark?.('sn:scene:start');
    setDialog((open) => (open === 'map' || open === 'sheet' || open === 'explorer' ? null : open));
    setBoot(false);
    if (animated) {
      setTransition((value) => ({ prev: current, dir, run: value.run + 1 }));
      setNeonPhase('off');
    } else {
      setTransition((value) => ({ prev: null, dir, run: value.run + 1 }));
      setNeonPhase('on');
    }
    setState((value) => ({ ...value, sceneId: next }));
    stateRef.current = { ...current, sceneId: next };
  }, []);

  const navigate = useCallback((target: ReferenceSceneId | number) => {
    const current = stateRef.current;
    const currentIndex = REFERENCE_SCENES.indexOf(current.sceneId);
    let nextIndex: number;
    let dir: 1 | -1;
    if (typeof target === 'string') {
      nextIndex = REFERENCE_SCENES.indexOf(target);
      dir = nextIndex >= currentIndex ? 1 : -1;
    } else {
      nextIndex = (currentIndex + target + REFERENCE_SCENES.length * 4) % REFERENCE_SCENES.length;
      dir = target >= 0 ? 1 : -1;
    }
    if (nextIndex < 0 || nextIndex === currentIndex) return;
    const next = REFERENCE_SCENES[nextIndex];

    navAbortRef.current?.abort();
    const controller = new AbortController();
    navAbortRef.current = controller;
    const animated = !reducedMotionRef.current && !compactModeRef.current;

    let settled = false;
    let timer = 0;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (controller.signal.aborted) return;
      if (navAbortRef.current === controller) navAbortRef.current = null;
      commitScene(next, dir, animated);
    };
    controller.signal.addEventListener('abort', () => window.clearTimeout(timer), { once: true });

    if (!animated) {
      finish();
      return;
    }
    const image = new Image();
    image.decoding = 'async';
    image.src = REFERENCE_RASTER_META[next].src;
    timer = window.setTimeout(finish, DECODE_RACE_MS);
    image.decode().then(finish, finish);
  }, [commitScene]);

  const endTransition = useCallback(() => {
    window.clearTimeout(enterTimerRef.current);
    rootRef.current?.removeAttribute('data-scene-enter');
    setTransition((value) => (value.prev ? { ...value, prev: null } : value));
    if (performance.getEntriesByName?.('sn:scene:start').length) {
      performance.mark?.('sn:scene:end');
      try {
        performance.measure?.('sn:scene', 'sn:scene:start', 'sn:scene:end');
      } catch {
        // Marks can be cleared by devtools; the measure is diagnostic only.
      }
      performance.clearMarks?.('sn:scene:start');
    }
  }, []);

  useEffect(() => {
    if (!transition.prev) return;
    rootRef.current?.setAttribute('data-scene-enter', '');
    window.clearTimeout(enterTimerRef.current);
    enterTimerRef.current = window.setTimeout(endTransition, reducedMotion ? ENTER_FALLBACK_REDUCED_MS : ENTER_FALLBACK_MS);
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setNeonPhase('on'));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
      window.clearTimeout(enterTimerRef.current);
    };
  }, [endTransition, reducedMotion, transition.run, transition.prev]);

  /* ── tour + nudges ── */
  const tour = useTour({ sceneId: state.sceneId, persona, navigate, update: patchState, reducedMotion });
  const tourRef = useRef(tour);
  tourRef.current = tour;
  const spotlight = tour.spotlight;

  const update = useCallback((patch: ReferenceDemoPatch) => {
    patchState(patch);
    tourRef.current.notifyInteraction();
    announce(describeState(mergeDemoPatch(stateRef.current, patch), lang));
  }, [announce, lang, patchState]);

  /* ── persona ── */
  const setPersona = useCallback((next: Persona) => {
    if (!isPersona(next) || next === personaRef.current) return;
    personaRef.current = next;
    setPersonaState(next);
    writeSession(PERSONA_KEY, next);
    patchState(personaPatch(next));
    const meta = personaMeta(next);
    announce(t.announcePersona(meta.label[lang], meta.focus[lang]));
  }, [announce, lang, patchState, t]);

  /* ── dialogs ── */
  const rememberFocus = useCallback(() => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, []);

  const openDialog = useCallback((kind: DialogKind) => {
    rememberFocus();
    setDialog(kind);
  }, [rememberFocus]);

  const closeDialog = useCallback(() => setDialog(null), []);

  const openMap = useCallback(() => openDialog('map'), [openDialog]);

  const openAssistant = useCallback((seed = '') => {
    setQuestionSeed(seed);
    setQuestion(seed);
    openDialog('assistant');
  }, [openDialog]);

  const closeAssistant = useCallback(() => {
    const activeController = abortRef.current;
    activeController?.abort();
    if (abortRef.current === activeController) abortRef.current = null;
    setExchanges((current) => current.map((item) => item.state === 'streaming'
      ? { ...item, answer: item.answer || CANCELLED[lang], state: 'cancelled' }
      : item));
    setBusy(false);
    setDialog(null);
  }, [lang]);

  const openHelp = useCallback(() => openDialog('help'), [openDialog]);

  const openExplorer = useCallback(() => {
    openDialog('explorer');
    announce(t.announceExplorerOpen(REFERENCE_SCENE_LABELS[stateRef.current.sceneId][lang]));
  }, [announce, lang, openDialog, t]);

  const closeExplorer = useCallback(() => {
    setDialog(null);
    announce(t.announceExplorerClose);
  }, [announce, t]);

  useModalLayer(dialog, dialogRef, returnFocusRef, transcriptRef, dialog === 'assistant' ? 'textarea' : FOCUSABLE_SELECTOR);

  /* ── deconstruct ── */
  const deconstructRef = useRef(false);
  const setDeconstruct = useCallback((open: boolean) => {
    if (deconstructRef.current === open) return;
    deconstructRef.current = open;
    const name = REFERENCE_SCENE_LABELS[stateRef.current.sceneId][lang];
    if (open) {
      rememberFocus();
      announce(t.announceDeconstruct(name));
    } else {
      announce(t.announceReference(name));
    }
    setDeconstructOpen(open);
  }, [announce, lang, rememberFocus, t]);

  useEffect(() => {
    if (deconstructOpen) {
      const timer = window.setTimeout(() => setDeconstructSettled(true), DECONSTRUCT_DISSOLVE_MS);
      const frame = requestAnimationFrame(() => semanticStageRef.current?.focus());
      return () => {
        window.clearTimeout(timer);
        cancelAnimationFrame(frame);
      };
    }
    setDeconstructSettled(false);
    const previous = returnFocusRef.current;
    returnFocusRef.current = null;
    if (!previous) return;
    const frame = requestAnimationFrame(() => requestAnimationFrame(() => {
      if (previous.isConnected) previous.focus();
      else transcriptRef.current?.focus();
    }));
    return () => cancelAnimationFrame(frame);
  }, [deconstructOpen]);

  useEffect(() => {
    const onDeconstruct = (event: Event) => {
      const detail = (event as CustomEvent<{ open?: boolean } | undefined>).detail;
      if (detail && typeof detail.open === 'boolean') setDeconstruct(detail.open);
      else setDeconstruct(!deconstructRef.current);
    };
    window.addEventListener('sn:deconstruct', onDeconstruct);
    return () => window.removeEventListener('sn:deconstruct', onDeconstruct);
  }, [setDeconstruct]);

  /* ── reset / fullscreen / share ── */
  const reset = useCallback(() => {
    abortRef.current?.abort();
    navAbortRef.current?.abort();
    tourRef.current.pause();
    setState(INITIAL_REFERENCE_STATE);
    stateRef.current = INITIAL_REFERENCE_STATE;
    setTransition((value) => ({ prev: null, dir: -1, run: value.run + 1 }));
    setNeonPhase('on');
    setExchanges([]);
    setVisitedScenes([INITIAL_REFERENCE_STATE.sceneId]);
    setBusy(false);
    setDialog(null);
    setDeconstructOpen(false);
    announce(t.announceReset);
  }, [announce, t]);

  const toggleFullscreen = useCallback(() => {
    void (async () => {
      try {
        if (document.fullscreenElement) await document.exitFullscreen();
        else await rootRef.current?.requestFullscreen();
      } catch {
        // Fullscreen is progressive enhancement; the fixed viewport remains usable.
      }
    })();
  }, []);

  useEffect(() => {
    const onChange = () => {
      const on = Boolean(document.fullscreenElement);
      setFullscreen(on);
      announce(on ? t.announceFullscreenOn : t.announceFullscreenOff);
    };
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [announce, t]);

  const share = useCallback(async (): Promise<string | null> => {
    const url = window.location.href;
    const n = REFERENCE_SCENES.indexOf(stateRef.current.sceneId) + 1;
    const lens = personaMeta(personaRef.current).label[lang];
    const nav = navigator as Navigator & { share?: (data: { url: string; title?: string }) => Promise<void> };
    if (touch && typeof nav.share === 'function') {
      try {
        await nav.share({ url, title: `StudyNinja · ${REFERENCE_SCENE_LABELS[stateRef.current.sceneId][lang]}` });
        showToast(t.toastShared(n, lens));
        return url;
      } catch {
        // The visitor dismissed the sheet or the platform refused; fall through to the clipboard.
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast(t.toastCopied(n, lens));
      return url;
    } catch {
      showToast(t.toastFailed(url));
      return null;
    }
  }, [lang, showToast, t, touch]);

  /* ── assistant ── */
  const executeAction = useCallback((action: ReferencePresentationAction) => {
    switch (action.type) {
      case 'navigate_to_scene': navigate(action.scene); break;
      case 'focus_student': update({ selectedStudent: action.student }); break;
      case 'select_cause': update({ causeStep: action.step }); break;
      case 'set_teacher_goal': update({ teacherGoal: action.goal }); break;
      case 'focus_graph_node': update({ selectedGraphNode: action.node, graphDrawerOpen: true }); break;
      case 'set_graph_layer': update({ graphLayers: { [action.layer]: action.visible } }); break;
      case 'set_profile': update({ profile: { [action.field]: action.value } }); break;
      case 'compare_routes': update({ compareRoute: action.enabled }); break;
      case 'set_cycle_step': update({ cycleStep: action.step }); break;
      case 'set_reward_mode': update({ rewardMode: action.mode }); break;
      case 'set_teacher_action': update({ teacherAction: action.action }); break;
      case 'set_explanation_depth': update({ explanationDepth: action.depth }); break;
    }
  }, [navigate, update]);

  const ask = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    const target = inferScene(trimmed);
    if (target) navigate(target);
    const inferredPatch = localPatch(trimmed);
    if (Object.keys(inferredPatch).length > 0) update(inferredPatch);

    const id = Date.now();
    setExchanges((current) => [...current, { id, question: trimmed, answer: '', state: 'streaming' }]);
    setQuestion('');
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch('/api/studyninja/ask', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          lang,
          message: trimmed,
          context: {
            sceneId: target ?? state.sceneId,
            viewerRole: persona,
            selectedStudent: state.selectedStudent,
            causeStep: state.causeStep,
            teacherGoal: state.teacherGoal,
            graphMode: state.graphMode,
            graphLayers: state.graphLayers,
            selectedGraphNode: state.selectedGraphNode,
            profile: state.profile,
            compareRoute: state.compareRoute,
            cycleStep: state.cycleStep,
            rewardMode: state.rewardMode,
            teacherAction: state.teacherAction,
            explanationDepth: state.explanationDepth,
            visitedScenes: target && !visitedScenes.includes(target) ? [...visitedScenes, target] : visitedScenes,
          },
          history: exchanges.filter((item) => item.state === 'done').slice(-4).map((item) => ({ question: item.question, answer: item.answer })),
        }),
        signal: controller.signal,
      });
      if (!response.ok || !response.body) throw new Error('offline');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';
      let terminal = false;
      let pendingActions: ReferencePresentationAction[] = [];
      for (;;) {
        const { done, value: chunk } = await reader.read();
        if (done) break;
        buffer += decoder.decode(chunk, { stream: true });
        let split: number;
        while ((split = buffer.indexOf('\n\n')) >= 0) {
          const event = buffer.slice(0, split);
          buffer = buffer.slice(split + 2);
          const line = event.split('\n').find((item) => item.startsWith('data:'));
          if (!line) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim()) as { type?: string; text?: string; actions?: unknown };
            if (payload.type === 'delta' && typeof payload.text === 'string') {
              answer += payload.text;
              setExchanges((current) => current.map((item) => item.id === id ? { ...item, answer } : item));
            }
            if (payload.type === 'actions') pendingActions = validateReferenceActions(payload.actions);
            if (payload.type === 'done') terminal = true;
            if (payload.type === 'error') throw new Error('interrupted');
          } catch {
            if (event.includes('"type":"error"')) throw new Error('interrupted');
          }
        }
      }
      if (!terminal) throw new Error('interrupted');
      setExchanges((current) => current.map((item) => item.id === id
        ? { ...item, answer: answer.trim() || FALLBACKS[lang], state: answer.trim() ? 'done' : 'error' }
        : item));
      pendingActions.forEach(executeAction);
    } catch (error) {
      if ((error as Error).name === 'AbortError') {
        setExchanges((current) => current.map((item) => item.id === id && item.state === 'streaming'
          ? { ...item, answer: item.answer || CANCELLED[lang], state: 'cancelled' }
          : item));
      } else {
        setExchanges((current) => current.map((item) => item.id === id
          ? { ...item, answer: FALLBACKS[lang], state: 'error' }
          : item));
      }
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setBusy(false);
      }
    }
  }, [busy, exchanges, executeAction, lang, navigate, persona, state, update, visitedScenes]);

  useEffect(() => () => {
    abortRef.current?.abort();
    navAbortRef.current?.abort();
  }, []);

  /* ── environment: reduced motion, coarse pointer, compact layout ── */
  useEffect(() => {
    const compact = window.matchMedia('(max-width: 720px)');
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarse = window.matchMedia('(pointer: coarse)');
    const sync = () => {
      setCompactMode(compact.matches);
      setReducedMotion(motion.matches);
      setTouch(coarse.matches);
    };
    sync();
    compact.addEventListener('change', sync);
    motion.addEventListener('change', sync);
    coarse.addEventListener('change', sync);
    return () => {
      compact.removeEventListener('change', sync);
      motion.removeEventListener('change', sync);
      coarse.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.overflow = bodyOverflow;
    };
  }, []);

  /* ── deep link in (mount) ── */
  useEffect(() => {
    const parsed = parseDeepLink(window.location.search);
    const params = new URLSearchParams(window.location.search);
    const stored = readSession(PERSONA_KEY);
    const initialPersona = parsed.persona ?? (isPersona(stored) ? stored : DEFAULT_PERSONA);
    if (initialPersona !== DEFAULT_PERSONA) {
      personaRef.current = initialPersona;
      setPersonaState(initialPersona);
      setState((current) => mergeDemoPatch(current, personaPatch(initialPersona)));
    }
    if (parsed.scene && parsed.scene !== INITIAL_REFERENCE_STATE.sceneId) {
      setState((current) => ({ ...current, sceneId: parsed.scene as ReferenceSceneId }));
      stateRef.current = { ...stateRef.current, sceneId: parsed.scene };
    }
    if (params.get(MODE_PARAM) === 'deconstruct') setDeconstructOpen(true);
    if (params.get(TOUR_PARAM) === '1') pendingTourRef.current = true;
    hydratedRef.current = true;
  }, []);

  /* ── deep link out (debounced replaceState, never a navigation) ── */
  useEffect(() => {
    if (!hydratedRef.current) return;
    const timer = window.setTimeout(() => {
      const query = buildDeepLink(window.location.search, state.sceneId, persona);
      const params = new URLSearchParams(query);
      if (deconstructOpen) params.set(MODE_PARAM, 'deconstruct');
      else params.delete(MODE_PARAM);
      params.delete(TOUR_PARAM);
      const search = params.toString();
      const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
      const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (next !== current) window.history.replaceState(window.history.state, '', next);
    }, 150);
    return () => window.clearTimeout(timer);
  }, [deconstructOpen, persona, state.sceneId]);

  /* ── boot flag ── */
  useEffect(() => {
    const timer = window.setTimeout(() => setBoot(false), BOOT_MS);
    return () => window.clearTimeout(timer);
  }, []);

  /* ── dock: shown after load and after every scene change ── */
  useEffect(() => {
    revealDock(DOCK_SHOW_MS);
  }, [revealDock, state.sceneId]);

  /* ── scene bookkeeping: visited list, announcement, key hint, strip sync ── */
  useEffect(() => {
    setVisitedScenes((current) => current.includes(state.sceneId) ? current : [...current, state.sceneId]);
  }, [state.sceneId]);

  useEffect(() => {
    if (!firstSceneAnnouncedRef.current) {
      firstSceneAnnouncedRef.current = true;
      return;
    }
    sceneChangesRef.current += 1;
    announce(t.announceScene(sceneIndex + 1, sceneName, personaLabel));
    if (sceneChangesRef.current === 1 && !touch && !compactMode && readSession(NUDGE_HINT_KEY) !== '1' && !tourRef.current.spotlight) {
      writeSession(NUDGE_HINT_KEY, '1');
      showToast(t.keysHint);
    }
    // The persona label is deliberately not a dependency: a lens change announces itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sceneId]);

  /* ── tour state mirrors: progress var, announcements, ?tour=1 ── */
  useEffect(() => {
    rootRef.current?.style.setProperty('--sn-tour-p', tour.progress.toFixed(4));
  }, [tour.progress]);

  const tourPlayingRef = useRef(false);
  useEffect(() => {
    if (tour.playing === tourPlayingRef.current) return;
    tourPlayingRef.current = tour.playing;
    announce(tour.playing ? t.announceTourStart : t.announceTourPause);
  }, [announce, t, tour.playing]);

  const [loadedScene, setLoadedScene] = useState<ReferenceSceneId | null>(null);
  const onFirstImageLoad = useCallback(() => {
    setLoadedScene(stateRef.current.sceneId);
    if (pendingTourRef.current) {
      pendingTourRef.current = false;
      if (!reducedMotionRef.current) window.setTimeout(() => tourRef.current.play(), 400);
    }
  }, []);

  useEffect(() => {
    // Neighbours (and their masks) once the current image has landed.
    if (!loadedScene) return;
    const index = REFERENCE_SCENES.indexOf(loadedScene);
    const neighbours = [
      REFERENCE_SCENES[(index + 1) % REFERENCE_SCENES.length],
      REFERENCE_SCENES[(index - 1 + REFERENCE_SCENES.length) % REFERENCE_SCENES.length],
    ];
    const images: HTMLImageElement[] = [];
    for (const scene of neighbours) {
      for (const src of [REFERENCE_RASTER_META[scene].src, LIGHT_META[scene].src]) {
        const image = new Image();
        image.decoding = 'async';
        image.src = src;
        images.push(image);
      }
    }
    return () => {
      for (const image of images) image.src = '';
    };
  }, [loadedScene]);

  useEffect(() => {
    // All masks, then (fine pointers on a fast link only) the remaining PNGs: sequentially, on idle.
    let cancelled = false;
    let handle = 0;
    let usedIdle = false;
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
    const fastLink = !connection?.saveData && (!connection?.effectiveType || connection.effectiveType === '4g');
    const wantsPngs = window.matchMedia('(min-width: 721px) and (pointer: fine)').matches && fastLink;
    const queue: string[] = [
      ...REFERENCE_SCENES.map((scene) => LIGHT_META[scene].src),
      ...(wantsPngs ? REFERENCE_SCENES.map((scene) => REFERENCE_RASTER_META[scene].src) : []),
    ];
    const step = () => {
      if (cancelled) return;
      const src = queue.shift();
      if (!src) return;
      const image = new Image();
      image.decoding = 'async';
      const next = () => {
        if (cancelled) return;
        if (typeof window.requestIdleCallback === 'function') {
          usedIdle = true;
          handle = window.requestIdleCallback(step, { timeout: 2000 });
        } else {
          usedIdle = false;
          handle = window.setTimeout(step, 120);
        }
      };
      image.onload = next;
      image.onerror = next;
      image.src = src;
    };
    if (typeof window.requestIdleCallback === 'function') {
      usedIdle = true;
      handle = window.requestIdleCallback(step, { timeout: 2000 });
    } else {
      handle = window.setTimeout(step, 2000);
    }
    return () => {
      cancelled = true;
      if (usedIdle && typeof window.cancelIdleCallback === 'function') window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  /* ── pointer bus: one passive listener, CSS vars via rAF, feeds dock presence ── */
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const viewport = viewportRef.current;
    const motionEnabled = !reducedMotion && !touch && !compactMode && viewport !== null;
    const target = { mx: 0.5, my: 0.5 };
    const current = { mx: 0.5, my: 0.5, dx: 0, dy: 0 };
    let rect: DOMRect | null = null;
    let inside = false;
    let frame = 0;

    const write = () => {
      if (!viewport) return;
      viewport.style.setProperty('--sn-mx', current.mx.toFixed(4));
      viewport.style.setProperty('--sn-my', current.my.toFixed(4));
      viewport.style.setProperty('--sn-dx', current.dx.toFixed(4));
      viewport.style.setProperty('--sn-dy', current.dy.toFixed(4));
    };

    const tick = () => {
      frame = 0;
      const frozen = !inside || dialogOpenRef.current;
      const tdx = frozen ? 0 : (target.mx - 0.5) * 2;
      const tdy = frozen ? 0 : (target.my - 0.5) * 2;
      current.mx += (target.mx - current.mx) * 0.18;
      current.my += (target.my - current.my) * 0.18;
      current.dx += (tdx - current.dx) * 0.08;
      current.dy += (tdy - current.dy) * 0.08;
      write();
      const delta = Math.max(
        Math.abs(target.mx - current.mx),
        Math.abs(target.my - current.my),
        Math.abs(tdx - current.dx),
        Math.abs(tdy - current.dy),
      );
      if (delta > 0.0005) frame = requestAnimationFrame(tick);
    };

    const kick = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    const onMove = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      revealDock(DOCK_HIDE_MS);
      if (!motionEnabled || !viewport) return;
      if (!rect) rect = viewport.getBoundingClientRect();
      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;
      inside = x >= 0 && x <= 1 && y >= 0 && y <= 1;
      target.mx = Math.min(1, Math.max(0, x));
      target.my = Math.min(1, Math.max(0, y));
      kick();
    };

    const onEnter = () => {
      rect = viewport?.getBoundingClientRect() ?? null;
      viewport?.style.setProperty('--sn-lens-on', '1');
    };

    const onLeave = () => {
      inside = false;
      viewport?.style.setProperty('--sn-lens-on', '0');
      target.mx = 0.5;
      target.my = 0.5;
      kick();
    };

    const onResize = () => {
      rect = null;
    };

    root.addEventListener('pointermove', onMove, { passive: true });
    viewport?.addEventListener('pointerenter', onEnter);
    viewport?.addEventListener('pointerleave', onLeave);
    window.addEventListener('resize', onResize);
    if (viewport) {
      viewport.style.setProperty('--sn-lens-on', '0');
      write();
    }

    return () => {
      root.removeEventListener('pointermove', onMove);
      viewport?.removeEventListener('pointerenter', onEnter);
      viewport?.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('resize', onResize);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [compactMode, reducedMotion, revealDock, touch]);

  /* ── --sn-unit on the viewport (lens card, chrome); each canvas owns its own ── */
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || typeof ResizeObserver === 'undefined') return;
    const write = (width: number) => viewport.style.setProperty(CANVAS_UNIT_VAR, `${(width / 100).toFixed(3)}px`);
    write(viewport.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) write(entry.contentRect.width);
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [compactMode]);

  /* ── keyboard: one capture-phase listener keyed on event.code ── */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.isComposing) return;
      const target = event.target as HTMLElement | null;
      const editing = Boolean(target?.closest('input, textarea, select, [contenteditable="true"]'));

      if (event.code === 'Escape') {
        if (dialog === 'help') closeDialog();
        else if (dialog === 'assistant') closeAssistant();
        else if (dialog === 'map') closeDialog();
        else if (dialog === 'sheet') closeDialog();
        else if (dialog === 'explorer') closeExplorer();
        else if (deconstructOpen) setDeconstruct(false);
        else if (tourRef.current.spotlight && !tourRef.current.playing) tourRef.current.dismissNudge();
        else if (document.fullscreenElement) void document.exitFullscreen();
        else return;
        event.preventDefault();
        return;
      }

      if (dialog !== null) {
        // Help toggles itself closed with ? as well.
        if (dialog === 'help' && event.code === 'Slash' && event.shiftKey && !editing) {
          event.preventDefault();
          closeDialog();
        }
        return;
      }
      if (editing) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const focusOnStage = target === document.body || target === rootRef.current || target === viewportRef.current || target === transcriptRef.current;
      const notify = () => tourRef.current.notifyInteraction();

      switch (event.code) {
        case 'ArrowRight':
          if (event.shiftKey) update(stepInScene(stateRef.current, 1));
          else { notify(); navigate(1); }
          break;
        case 'ArrowLeft':
          if (event.shiftKey) update(stepInScene(stateRef.current, -1));
          else { notify(); navigate(-1); }
          break;
        case 'Home': notify(); navigate(REFERENCE_SCENES[0]); break;
        case 'End': notify(); navigate(REFERENCE_SCENES[REFERENCE_SCENES.length - 1]); break;
        case 'Space':
          if (!focusOnStage) return;
          tourRef.current.toggle();
          break;
        case 'KeyP': {
          const index = PERSONAS.indexOf(personaRef.current);
          setPersona(PERSONAS[(index + 1) % PERSONAS.length]);
          break;
        }
        case 'KeyM': openMap(); break;
        case 'KeyQ': openAssistant(); break;
        case 'KeyF': toggleFullscreen(); break;
        case 'KeyR': reset(); break;
        case 'KeyD': {
          const focused = document.activeElement instanceof HTMLElement ? document.activeElement.closest<HTMLElement>('[data-hotspot]') : null;
          if (focused && !deconstructOpen) focused.click();
          notify();
          setDeconstruct(!deconstructOpen);
          break;
        }
        case 'KeyS': void share(); break;
        case 'Slash':
          if (!event.shiftKey) return;
          openHelp();
          break;
        default: {
          if (!/^Digit[0-9]$/.test(event.code)) return;
          const digit = Number(event.code.slice(5));
          notify();
          navigate(REFERENCE_SCENES[digit === 0 ? 9 : digit - 1]);
          break;
        }
      }
      event.preventDefault();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [closeAssistant, closeDialog, closeExplorer, deconstructOpen, dialog, navigate, openAssistant, openHelp, openMap, reset, setDeconstruct, setPersona, share, toggleFullscreen, update]);

  /* ── mobile strip: IntersectionObserver syncs the scene, scrollIntoView follows state ── */
  useEffect(() => {
    if (!compactMode) return;
    const strip = stripRef.current;
    if (!strip || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver((entries) => {
      if (stripProgrammaticRef.current) return;
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const id = (entry.target as HTMLElement).dataset.scene as ReferenceSceneId | undefined;
        if (id && id !== stateRef.current.sceneId) {
          tourRef.current.notifyInteraction();
          navigate(id);
        }
      }
    }, { root: strip, threshold: 0.6 });
    for (const slide of Array.from(strip.children)) observer.observe(slide);
    return () => observer.disconnect();
  }, [compactMode, navigate]);

  useEffect(() => {
    if (!compactMode) return;
    const strip = stripRef.current;
    const slide = strip?.querySelector<HTMLElement>(`[data-scene="${state.sceneId}"]`);
    if (!strip || !slide) return;
    const expectedLeft = slide.offsetLeft - (strip.clientWidth - slide.clientWidth) / 2;
    if (Math.abs(strip.scrollLeft - expectedLeft) < 4) return;
    stripProgrammaticRef.current = true;
    window.clearTimeout(stripTimerRef.current);
    const release = () => {
      stripProgrammaticRef.current = false;
      window.clearTimeout(stripTimerRef.current);
      strip.removeEventListener('scrollend', release);
    };
    strip.addEventListener('scrollend', release);
    stripTimerRef.current = window.setTimeout(release, 400);
    slide.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', inline: 'center', block: 'nearest' });
    return () => {
      strip.removeEventListener('scrollend', release);
      window.clearTimeout(stripTimerRef.current);
    };
  }, [compactMode, reducedMotion, state.sceneId]);

  /* ── explorer: open centred on the persona's try-this hotspot ── */
  useEffect(() => {
    if (dialog !== 'explorer') return;
    const id = LENS_BOOK[state.sceneId][persona].tryThis.hotspot;
    const frame = requestAnimationFrame(() => {
      const scroller = explorerScrollRef.current;
      const hotspot = scroller?.querySelector<HTMLElement>(`[data-hotspot="${id}"]`);
      if (!scroller || !hotspot) return;
      scroller.scrollTo({
        left: hotspot.offsetLeft + hotspot.offsetWidth / 2 - scroller.clientWidth / 2,
        top: hotspot.offsetTop + hotspot.offsetHeight / 2 - scroller.clientHeight / 2,
        behavior: 'auto',
      });
    });
    return () => cancelAnimationFrame(frame);
    // Only the opening moment matters; later scene/persona changes close the explorer anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialog]);

  /* ── living context ── */
  const living = useMemo<LivingContextValue>(() => ({
    lang,
    persona,
    setPersona,
    spotlight,
    dismissNudge: tour.dismissNudge,
    tour: {
      playing: tour.playing,
      progress: tour.progress,
      beat: tour.beat,
      play: tour.play,
      pause: tour.pause,
      toggle: tour.toggle,
    },
    sceneIndex,
    navigate,
    openAssistant,
    toggleFullscreen,
    openMap,
    reset,
    share,
    reducedMotion,
    touch,
  }), [lang, navigate, openAssistant, openMap, persona, reducedMotion, reset, sceneIndex, setPersona, share, spotlight, toggleFullscreen, touch, tour.beat, tour.dismissNudge, tour.pause, tour.play, tour.playing, tour.progress, tour.toggle]);

  /* ── derived: spotlight geometry for veil and push ── */
  const sceneProps = {
    lang,
    state,
    update,
    openAssistant,
    navigate,
    reset,
    onInteraction: () => tourRef.current.notifyInteraction(),
  };

  const spotGeometry = useMemo(() => {
    if (!spotlight) return null;
    const hotspot = hotspotsFor({ lang, state, update: () => undefined, openAssistant: () => undefined, navigate: () => undefined, reset: () => undefined })
      .find((item) => item.id === spotlight);
    if (!hotspot) return null;
    const canvas = SCENE_CANVAS[state.sceneId];
    const cx = (hotspot.rect[0] + hotspot.rect[2] / 2) / canvas.width;
    const cy = (hotspot.rect[1] + hotspot.rect[3] / 2) / canvas.height;
    const point = canvasToViewport(state.sceneId, cx, cy);
    const radius = Math.max(6, (Math.max(hotspot.rect[2], hotspot.rect[3]) / canvas.width) * 100 * 0.9 * point.scale);
    return { x: point.x, y: point.y, r: radius };
  }, [lang, spotlight, state]);

  const tourSpot = tour.playing && spotGeometry !== null && !reducedMotion;
  const veilStyle = {
    '--sp-x': `${(spotGeometry?.x ?? 50).toFixed(2)}%`,
    '--sp-y': `${(spotGeometry?.y ?? 50).toFixed(2)}%`,
    '--sp-r': `${(spotGeometry?.r ?? 6).toFixed(2)}%`,
    '--sn-veil': tour.playing && spotGeometry ? '0.42' : '0',
  } as CSSProperties;
  const pushStyle = {
    '--sn-ox': `${(spotGeometry?.x ?? 50).toFixed(2)}%`,
    '--sn-oy': `${(spotGeometry?.y ?? 50).toFixed(2)}%`,
  } as CSSProperties;

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void ask(question);
  };

  const keyHint = !touch && !compactMode;
  const tourCaption = tour.caption?.[lang] ?? LENS_BOOK[state.sceneId][persona].insight[lang];
  const anyDialog = dialog !== null;

  /* ── mobile per-scene controls (sheet) ── */
  const mobileCopy = (ru: string, en: string) => lang === 'ru' ? ru : en;
  const mobileStatus = describeState(state, lang);
  let mobileControls: Array<{ label: string; activate: () => void }> = [];
  switch (state.sceneId) {
    case 'classroom':
      mobileControls = [
        { label: mobileCopy('Предыдущий ученик', 'Previous learner'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующий ученик', 'Next learner'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'motivation':
      mobileControls = [
        { label: mobileCopy('Предыдущее звено', 'Previous cause'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующее звено', 'Next cause'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'teacher-goal':
      mobileControls = [
        { label: mobileCopy('Предыдущая цель', 'Previous goal'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующая цель', 'Next goal'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'knowledge-map':
      mobileControls = [
        { label: mobileCopy(state.graphLayers.errors ? 'Скрыть слой ошибок' : 'Показать слой ошибок', state.graphLayers.errors ? 'Hide errors layer' : 'Show errors layer'), activate: () => update({ graphLayers: { errors: !state.graphLayers.errors } }) },
        { label: mobileCopy(state.graphDrawerOpen ? 'Закрыть карточку узла' : 'Открыть карточку узла', state.graphDrawerOpen ? 'Close node details' : 'Open node details'), activate: () => update({ graphDrawerOpen: !state.graphDrawerOpen }) },
      ];
      break;
    case 'personal-route':
      mobileControls = [
        { label: mobileCopy('Изменить уверенность', 'Change confidence'), activate: () => update({ profile: { confidence: (state.profile.confidence + 1) % 7 } }) },
        { label: mobileCopy(state.compareRoute ? 'Скрыть сравнение' : 'Сравнить маршруты', state.compareRoute ? 'Hide comparison' : 'Compare routes'), activate: () => update({ compareRoute: !state.compareRoute }) },
      ];
      break;
    case 'learning-cycle':
      mobileControls = [
        { label: mobileCopy('Предыдущий этап', 'Previous stage'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующий этап', 'Next stage'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'agency':
      mobileControls = [
        { label: mobileCopy('Награда за действие', 'Reward action'), activate: () => update({ rewardMode: 'action' }) },
        { label: mobileCopy('Награда за освоение', 'Reward mastery'), activate: () => update({ rewardMode: 'mastery' }) },
      ];
      break;
    case 'teacher-workspace':
      mobileControls = [
        { label: mobileCopy('Предыдущее действие', 'Previous action'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующее действие', 'Next action'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'technology':
      mobileControls = [
        { label: mobileCopy('Предыдущая глубина', 'Previous depth'), activate: () => update(stepInScene(state, -1)) },
        { label: mobileCopy('Следующая глубина', 'Next depth'), activate: () => update(stepInScene(state, 1)) },
      ];
      break;
    case 'finale':
      mobileControls = [
        { label: mobileCopy('Спросить о маршруте', 'Ask about the route'), activate: () => openAssistant(mobileCopy('Почему выбран именно этот маршрут?', 'Why was this route selected?')) },
        { label: mobileCopy('Начать сначала', 'Start over'), activate: reset },
      ];
      break;
  }

  /* ── shared pieces ── */
  const semanticStage = (
    <div
      ref={semanticStageRef}
      className={styles.semanticStage}
      data-testid="sn-deconstruct"
      aria-hidden={!deconstructOpen}
      inert={deconstructOpen ? undefined : true}
      tabIndex={deconstructOpen ? -1 : undefined}
      aria-label={lang === 'ru' ? `Разбор сцены: ${sceneName}` : `Deconstructed scene: ${sceneName}`}
    >
      <div className={styles.semanticCanvas}>
        {deconstructOpen && (
          <ReferenceStage
            lang={lang}
            state={state}
            update={update}
            openAssistant={openAssistant}
            navigate={navigate}
            reset={reset}
            onInteraction={(label) => announce(lang === 'ru' ? `Действие: ${label}` : `Action: ${label}`)}
          />
        )}
      </div>
    </div>
  );

  const backToReference = deconstructOpen && (
    <button type="button" className={styles.breadcrumb} data-testid="sn-back-to-reference" onClick={() => setDeconstruct(false)}>
      <span aria-hidden="true">←</span> {t.deconstructCrumb} · {String(sceneIndex + 1).padStart(2, '0')} {sceneName}
      <span className={styles.srOnly}> · {t.backToReference}</span>
    </button>
  );

  const personaChips = (
    <div className={styles.personaChips} role="radiogroup" aria-label={t.personaGroup}>
      {PERSONA_META.map((meta) => (
        <button
          key={meta.id}
          type="button"
          role="radio"
          aria-checked={meta.id === persona}
          data-persona={meta.id}
          onClick={() => setPersona(meta.id)}
        >
          {meta.label[lang]}
        </button>
      ))}
    </div>
  );

  const sceneMapDialog = dialog === 'map' && (
    <div className={styles.scrim} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDialog(); }}>
      <section ref={dialogRef} className={styles.sceneMap} role="dialog" aria-modal="true" aria-labelledby="reference-map-title" tabIndex={-1} onKeyDown={trapDialogFocus}>
        <header><span id="reference-map-title">{t.mapTitle}</span><button type="button" onClick={closeDialog} aria-label={t.close}>×</button></header>
        {ACTS.map((act) => (
          <section key={act.en} className={styles.act} aria-label={act[lang]}>
            <h3>{act[lang]}</h3>
            <ol>
              {act.scenes.map((index) => {
                const id = REFERENCE_SCENES[index];
                return (
                  <li key={id} style={{ '--i': index } as CSSProperties}>
                    <button type="button" aria-current={id === state.sceneId ? 'step' : undefined} onClick={() => { navigate(id); closeDialog(); }}>
                      <span>{String(index + 1).padStart(2, '0')}</span>
                      <strong>{REFERENCE_SCENE_LABELS[id][lang]}</strong>
                      <em>{LENS_BOOK[id][persona].headline[lang]}</em>
                    </button>
                  </li>
                );
              })}
            </ol>
          </section>
        ))}
      </section>
    </div>
  );

  const assistantDialog = dialog === 'assistant' && (
    <div className={styles.scrim} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeAssistant(); }}>
      <section ref={dialogRef} className={styles.assistant} role="dialog" aria-modal="true" aria-labelledby="reference-assistant-title" tabIndex={-1} onKeyDown={trapDialogFocus}>
        <header>
          <div>
            <small>{t.scene} {String(sceneIndex + 1).padStart(2, '0')} · {t.lens}: {personaLabel}</small>
            <h2 id="reference-assistant-title">{t.assistantTitle}</h2>
          </div>
          <button type="button" onClick={closeAssistant} aria-label={t.close}>×</button>
        </header>
        <div className={styles.exchangeLog} aria-live="polite">
          {exchanges.length === 0 && <p>{t.assistantEmpty}</p>}
          {exchanges.map((exchange) => <article key={exchange.id}><b>{exchange.question}</b><p>{exchange.answer || t.thinking}</p></article>)}
        </div>
        <form onSubmit={submit}>
          <p className={styles.aiNotice}>{t.aiNotice}</p>
          <textarea value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={1800} placeholder={questionSeed || t.placeholder} />
          <button type="submit" disabled={busy || !question.trim()} aria-label={t.send}>↑</button>
        </form>
      </section>
    </div>
  );

  const helpRows: Array<{ group: keyof typeof t.helpGroups; keys: string[]; label: string }> = compactMode
    ? [
      { group: 'nav', keys: ['←', '→'], label: t.help.swipe },
      { group: 'nav', keys: ['tap'], label: t.help.tap },
      { group: 'nav', keys: ['pinch'], label: t.help.pinch },
      { group: 'story', keys: ['▶'], label: t.help.space },
      { group: 'story', keys: ['⋯'], label: t.help.deconstruct },
      { group: 'lens', keys: ['⊙'], label: t.help.persona },
      { group: 'lens', keys: ['AI'], label: t.help.ask },
      { group: 'share', keys: ['↗'], label: t.help.share },
    ]
    : [
      { group: 'nav', keys: ['←', '→'], label: t.help.arrows },
      { group: 'nav', keys: ['1', '…', '0'], label: t.help.digits },
      { group: 'nav', keys: ['Home', 'End'], label: t.help.homeEnd },
      { group: 'nav', keys: ['Shift', '←/→'], label: t.help.shiftArrows },
      { group: 'nav', keys: ['M'], label: t.help.map },
      { group: 'story', keys: ['Space'], label: t.help.space },
      { group: 'story', keys: ['D'], label: t.help.deconstruct },
      { group: 'story', keys: ['R'], label: t.help.reset },
      { group: 'lens', keys: ['P'], label: t.help.persona },
      { group: 'lens', keys: ['Q'], label: t.help.ask },
      { group: 'lens', keys: ['F'], label: t.help.fullscreen },
      { group: 'share', keys: ['S'], label: t.help.share },
      { group: 'share', keys: ['?'], label: t.help.help },
      { group: 'share', keys: ['Esc'], label: t.help.esc },
    ];

  const helpDialog = dialog === 'help' && (
    <div className={styles.scrim} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDialog(); }}>
      <section ref={dialogRef} className={styles.help} data-testid="sn-help" role="dialog" aria-modal="true" aria-labelledby="reference-help-title" tabIndex={-1} onKeyDown={trapDialogFocus}>
        <header><span id="reference-help-title">{t.helpTitle}</span><button type="button" onClick={closeDialog} aria-label={t.close}>×</button></header>
        <div className={styles.helpGroups}>
          {(Object.keys(t.helpGroups) as Array<keyof typeof t.helpGroups>).map((group) => {
            const rows = helpRows.filter((row) => row.group === group);
            if (rows.length === 0) return null;
            return (
              <section key={group} aria-labelledby={`sn-help-${group}`}>
                <h3 id={`sn-help-${group}`}>{t.helpGroups[group]}</h3>
                <ul>
                  {rows.map((row, index) => (
                    <li key={row.label} style={{ '--i': Math.min(index, 12) } as CSSProperties}>
                      <span className={styles.kbdCluster}>{row.keys.map((key) => <kbd key={key}>{key}</kbd>)}</span>
                      <span>{row.label}</span>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </section>
    </div>
  );

  const toastNode = toast && (
    <div key={toast.id} className={styles.toast} data-testid="sn-toast" role="status">{toast.text}</div>
  );

  /* ── mobile tree ── */
  if (compactMode) {
    const sheetDialog = dialog === 'sheet' && (
      <div className={styles.scrim} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeDialog(); }}>
        <section ref={dialogRef} className={styles.sheet} role="dialog" aria-modal="true" aria-labelledby="sn-sheet-title" tabIndex={-1} onKeyDown={trapDialogFocus}>
          <span className={styles.sheetHandle} aria-hidden="true" />
          <header><span id="sn-sheet-title">{t.sheetTitle}</span><button type="button" onClick={closeDialog} aria-label={t.close}>×</button></header>
          <div id="mobile-scene-controls" className={styles.mobileSceneControls}>
            <p>{mobileStatus}</p>
            <div className={styles.mobileControlGrid}>
              {mobileControls.map((control) => <button key={control.label} type="button" onClick={control.activate}>{control.label}</button>)}
            </div>
          </div>
          <div className={styles.sheetActions}>
            <button type="button" onClick={() => { closeDialog(); setDeconstruct(true); }}>{t.deconstructLabel}</button>
            <button type="button" onClick={() => setDialog('map')}>{t.allScenes}</button>
            <button type="button" onClick={reset}>{t.help.reset}</button>
            <button type="button" onClick={() => setDialog('help')}>{t.helpTitle}</button>
          </div>
        </section>
      </div>
    );

    const explorerDialog = dialog === 'explorer' && (
      <div className={styles.scrim} role="presentation">
        <section ref={dialogRef} className={styles.explorer} data-testid="sn-mobile-explorer" role="dialog" aria-modal="true" aria-label={`${t.explorerTitle}: ${sceneName}`} tabIndex={-1} onKeyDown={trapDialogFocus}>
          <button type="button" className={styles.explorerClose} onClick={closeExplorer} aria-label={t.explorerClose}>×</button>
          <div ref={explorerScrollRef} className={styles.explorerScroll}>
            <div className={styles.explorerCanvas} style={{ aspectRatio: `${SCENE_CANVAS[state.sceneId].width} / ${SCENE_CANVAS[state.sceneId].height}` }}>
              <ReferenceRaster
                {...sceneProps}
                persona={persona}
                spotlight={spotlight ?? LENS_BOOK[state.sceneId][persona].tryThis.hotspot}
                neonPhase="on"
              />
            </div>
          </div>
        </section>
      </div>
    );

    return (
      <LivingContext.Provider value={living}>
        <section
          ref={rootRef}
          className={styles.experience}
          data-scene={state.sceneId}
          data-mode={deconstructOpen ? 'deconstruct' : 'reference'}
          data-dock="pinned"
          data-tour={tour.playing ? 'playing' : tour.progress > 0 ? 'paused' : 'idle'}
          data-input="coarse"
          data-layout="compact"
          data-dialog={anyDialog ? 'open' : undefined}
          data-boot={boot ? '' : undefined}
        >
          <a className={styles.skipLink} href="#reference-transcript">{t.skipTranscript}</a>
          <article ref={transcriptRef} id="reference-transcript" tabIndex={-1} className={styles.srTranscript}>
            <h1>{transcript.title}</h1>
            <p>{transcript.summary}</p>
          </article>
          <p className={styles.persistentStatus} aria-live="polite" aria-atomic="true">{announcement}</p>

          <header className={styles.mobileHeader}>
            <div className={styles.mobileIndex}>
              <b>{String(sceneIndex + 1).padStart(2, '0')} / 10</b>
              <span>{sceneName}</span>
            </div>
            {personaChips}
          </header>

          <div
            ref={stripRef}
            className={styles.mobileStrip}
            data-testid="sn-mobile-strip"
            role="group"
            aria-roledescription={t.sceneRole}
            aria-label={`${sceneIndex + 1}. ${sceneName} · ${t.lens}: ${personaLabel}`}
            aria-describedby="reference-transcript"
          >
            {REFERENCE_SCENES.map((id, index) => {
              const active = id === state.sceneId;
              const near = Math.abs(index - sceneIndex) <= 1;
              const meta = REFERENCE_RASTER_META[id];
              return (
                <div
                  key={id}
                  className={styles.slide}
                  data-scene={id}
                  data-active={active ? 'true' : undefined}
                  style={{ aspectRatio: `${meta.width} / ${meta.height}` }}
                >
                  {near ? (
                    <>
                      <img src={meta.src} width={meta.width} height={meta.height} alt="" loading={active ? 'eager' : 'lazy'} decoding="async" fetchPriority={active ? 'high' : 'auto'} draggable={false} onLoad={active ? onFirstImageLoad : undefined} />
                      {active && (
                        <div className={styles.slideNeon} data-testid="sn-neon" aria-hidden="true">
                          <NeonLayer scene={id} phase="on" />
                        </div>
                      )}
                    </>
                  ) : (
                    <span className={styles.slideIndex}>{String(index + 1).padStart(2, '0')}</span>
                  )}
                  {active && (
                    <button type="button" className={styles.slideExplore} onClick={openExplorer} aria-label={`${t.explorerTitle}: ${sceneName}`} />
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.dots} role="group" aria-label={t.dots}>
            {REFERENCE_SCENES.map((id, index) => (
              <button key={id} type="button" aria-label={t.goToScene(index + 1, REFERENCE_SCENE_LABELS[id][lang])} aria-current={id === state.sceneId ? 'step' : undefined} onClick={() => navigate(id)}>
                <i aria-hidden="true" />
              </button>
            ))}
          </div>

          <div className={styles.mobileLens}>
            <LensCard sceneId={state.sceneId} />
          </div>

          <section className={styles.mobileGuide} aria-label={lang === 'ru' ? 'Описание сцены' : 'Scene description'}>
            <h1>{transcript.title}</h1>
            <p>{transcript.summary}</p>
          </section>

          <nav className={styles.mobileBar} aria-label={lang === 'ru' ? 'Мобильное управление сценой' : 'Mobile scene controls'}>
            <button type="button" onClick={() => navigate(-1)} aria-label={t.prev}>←</button>
            <button type="button" onClick={tour.toggle} aria-label={tour.playing ? t.pause : t.play} aria-pressed={tour.playing}>{tour.playing ? '❚❚' : '▶'}</button>
            <button type="button" className={styles.mobileAsk} onClick={() => openAssistant()}>{t.ask}</button>
            <button type="button" onClick={() => void share()} aria-label={t.share}>↗</button>
            <button type="button" onClick={() => openDialog('sheet')} aria-label={t.more} aria-haspopup="dialog">⋯</button>
          </nav>

          {deconstructOpen && (
            <div className={styles.mobileDeconstruct}>
              {backToReference}
              {semanticStage}
            </div>
          )}

          <div id="sn-dock-slot" className={styles.dockSlot}>
            <Dock sceneId={state.sceneId} visible />
          </div>

          {toastNode}
          {sheetDialog}
          {explorerDialog}
          {sceneMapDialog}
          {assistantDialog}
          {helpDialog}
        </section>
      </LivingContext.Provider>
    );
  }

  /* ── desktop tree ── */
  return (
    <LivingContext.Provider value={living}>
      <section
        ref={rootRef}
        className={styles.experience}
        data-scene={state.sceneId}
        data-mode={deconstructOpen ? 'deconstruct' : 'reference'}
        data-dock={touch ? 'pinned' : dockVisible ? 'shown' : 'hidden'}
        data-tour={tour.playing ? 'playing' : tour.progress > 0 ? 'paused' : 'idle'}
        data-input={touch ? 'coarse' : 'fine'}
        data-layout="wide"
        data-dialog={anyDialog ? 'open' : undefined}
        data-fullscreen={fullscreen ? '' : undefined}
        data-boot={boot ? '' : undefined}
      >
        <a className={styles.skipLink} href="#reference-transcript">{t.skipTranscript}</a>
        <a className={styles.skipLink} href="#sn-dock-slot">{t.skipDock}</a>
        <article ref={transcriptRef} id="reference-transcript" tabIndex={-1} className={styles.srTranscript}>
          <h1>{transcript.title}</h1>
          <p>{transcript.summary}</p>
        </article>
        <p className={styles.persistentStatus} aria-live="polite" aria-atomic="true">{announcement}</p>

        <div
          ref={viewportRef}
          id="reference-scene"
          className={styles.viewport}
          data-testid="sn-viewport"
          role="group"
          aria-roledescription={t.sceneRole}
          aria-label={`${sceneIndex + 1}. ${sceneName} · ${t.lens}: ${personaLabel}`}
          aria-describedby="reference-transcript"
          style={{ '--sn-dir': String(transition.dir) } as CSSProperties}
          onPointerDown={() => tourRef.current.notifyInteraction()}
          onWheel={() => tourRef.current.notifyInteraction()}
          onTouchStart={() => tourRef.current.notifyInteraction()}
        >
          {semanticStage}

          <div className={styles.living} inert={deconstructOpen && deconstructSettled ? true : undefined} aria-hidden={deconstructOpen || undefined}>
            <div className={styles.parallax}>
              <div className={`${styles.push} ${tourSpot ? styles.pushActive : ''}`} style={pushStyle}>
                {transition.prev && (
                  <ReferenceRaster
                    key={`out-${transition.run}`}
                    lang={lang}
                    state={transition.prev}
                    update={() => undefined}
                    openAssistant={() => undefined}
                    navigate={() => undefined}
                    reset={() => undefined}
                    persona={persona}
                    spotlight={null}
                    neonPhase="on"
                    role="out"
                    interactive={false}
                  />
                )}
                <ReferenceRaster
                  key={`in-${state.sceneId}`}
                  {...sceneProps}
                  persona={persona}
                  spotlight={spotlight}
                  neonPhase={neonPhase}
                  role={transition.prev ? 'in' : undefined}
                  priority
                  keyHint={keyHint}
                  onImageLoad={onFirstImageLoad}
                  onEnterEnd={endTransition}
                />
              </div>
            </div>
            <div className={styles.veil} style={veilStyle} aria-hidden="true" />
            {!reducedMotion && <div key={transition.run} className={styles.sweep} data-run={transition.run} aria-hidden="true" />}
            <div className={styles.lensSlot}>
              <LensCard sceneId={state.sceneId} />
            </div>
          </div>

          {backToReference}
        </div>

        <button className={`${styles.edge} ${styles.edgeLeft}`} type="button" onClick={() => navigate(-1)} aria-label={t.prev}>←</button>
        <button className={`${styles.edge} ${styles.edgeRight}`} type="button" onClick={() => navigate(1)} aria-label={t.next}>→</button>

        {tour.playing && (
          <div className={styles.tourBar} data-testid="sn-tour-bar" role="group" aria-label={t.tourBar}>
            <div className={styles.tourTrack} aria-hidden="true"><i className={styles.tourFill} /></div>
            <p key={`${state.sceneId}-${tour.beat}`} className={styles.tourCaption} aria-hidden="true">{tourCaption}</p>
          </div>
        )}

        <div id="sn-dock-slot" className={styles.dockSlot}>
          <Dock sceneId={state.sceneId} visible={touch || dockVisible} />
        </div>

        {toastNode}
        {sceneMapDialog}
        {assistantDialog}
        {helpDialog}
      </section>
    </LivingContext.Provider>
  );
}
